import * as vp from 'vopay';

import type { BillingData } from './Billing/Billing';
import {
  PLAN_MODEL_KEYS,
  railDisplayLabel,
  isPermissionAllowedForMarket,
} from './Billing/PackageDetails/utils/planSettingHelpers';

type PlanGroupFee = vp.PlanFee<string> | vp.PlanFee<boolean> | vp.TierFee<string>;

// A single detected difference between the package default and the submitted value. `group` is the
// rail/service sub-heading a plan-setting change is nested under (undefined for the flat Agreement
// Details / Account Configuration sections), which is what disambiguates same-labelled fees.
type Change = { label: string; type: string; from: string; to: string; group?: string };

// The backend rejects these characters in the notes field (INVALID_CHARACTERS_REGEX). Strip them
// from the report so appending it can't fail validation. Line breaks and leading indentation are
// preserved; only runs of spaces *within* a line (e.g. left by char replacement) are collapsed.
const INVALID_CHARS_REGEX = /[%\\/<>&]/g;
const stripInvalid = (text: string): string =>
  text.replace(INVALID_CHARS_REGEX, ' ').replace(/(?<=\S)[^\S\n]{2,}/g, ' ');

type OptionMap =
  | { [optionKey: string]: { value: string | boolean; label: string } }
  | vp.TierOption<string | boolean>[]
  | undefined;

type FieldValue = string | boolean | null | undefined;

function optionLabel(value: FieldValue, options: OptionMap): string | null {
  if (!options) return null;
  const opts = Array.isArray(options) ? options : Object.values(options);
  return opts.find((o) => String(o.value) === String(value))?.label ?? null;
}

// Comparable form — collapses equivalent representations (null≈false for booleans, "1.00000"≈"1.00"≈"1").
function normalize(type: string, value: FieldValue): string {
  if (type === 'boolean') return value === true || value === 'true' ? 'true' : 'false';
  if (value === null || value === undefined) return '';
  if (['amount', 'percent', 'numeric'].includes(type)) {
    const sanitized = vp.sanitizeToNumeric(String(value)) ?? '';
    if (sanitized === '') return '';
    // Parse to a number so differing decimal representations of the same value (e.g. a package
    // default of "1.00000" vs a submitted "1.00") don't register as a change.
    const numeric = Number(sanitized);
    return Number.isNaN(numeric) ? sanitized : String(numeric);
  }
  return String(value);
}

// Human-readable form for the report (option labels where available).
function displayValue(type: string, value: FieldValue, options: OptionMap): string {
  if (type === 'boolean') return value === true || value === 'true' ? 'On' : 'Off';
  if (value === null || value === undefined || value === '') return '(empty)';
  const label = optionLabel(value, options);
  if (label) return label;
  if (['amount', 'percent', 'numeric'].includes(type)) return vp.sanitizeToNumeric(String(value)) ?? String(value);
  return String(value);
}

function diffField(
  label: string,
  type: string,
  originalValue: FieldValue,
  submittedValue: FieldValue,
  options: OptionMap,
): Change | null {
  if (normalize(type, originalValue) === normalize(type, submittedValue)) return null;
  return {
    label,
    type,
    from: displayValue(type, originalValue, options),
    to: displayValue(type, submittedValue, options),
  };
}

// AgreementDetails / AccountConfiguration: { [label]: BillingSettingItem } keyed by item.settings.
// Mirrors the tabs' hide logic: `item.visible` hides a field outright, and (Account Configuration
// only) a non-boolean/non-select field is gated by the nearest preceding boolean toggle — when that
// toggle is off the field is hidden, so its change must not reach the report.
function settingsItemChanges(
  original: { [key: string]: vp.BillingSettingItem } | undefined,
  submitted: Record<string, string | boolean> | undefined,
  { hasControllingBoolean = false }: { hasControllingBoolean?: boolean } = {},
): Change[] {
  if (!original || !submitted) return [];
  const changes: Change[] = [];
  let lastBooleanKey: string | null = null;
  for (const [label, item] of Object.entries(original)) {
    // Track the controlling boolean before any skip so ordering matches the form's field map.
    const controllingBoolean =
      hasControllingBoolean && item.type !== 'boolean' && item.type !== 'select' ? lastBooleanKey : null;
    if (item.type === 'boolean') lastBooleanKey = item.settings;

    if (!item.visible) continue;
    if (controllingBoolean && !submitted[controllingBoolean]) continue;
    if (!(item.settings in submitted)) continue;
    const change = diffField(label, item.type, item.value, submitted[item.settings], item.options);
    if (change) changes.push(change);
  }
  return changes;
}

// Emit a diff for each key of a fee/tier group. `visible` gates the whole group so fields that
// the form hid (permission off, wrong plan model, progressive pricing off) never reach the report.
function groupChanges(
  group: { [key: string]: PlanGroupFee },
  submitted: Record<string, string | boolean>,
  visible: boolean,
  labelFor: (fee: PlanGroupFee) => string,
): Change[] {
  if (!visible) return [];
  const changes: Change[] = [];
  for (const [key, fee] of Object.entries(group)) {
    if (!(key in submitted)) continue;
    const change = diffField(labelFor(fee), fee.type, fee.value, submitted[key], fee.options);
    if (change) changes.push(change);
  }
  return changes;
}

// AccountSettings / PaymentRails / AdditionalServices: PlanSettings sections of fees/tiers.
// Gating mirrors PlanSettingCard's showField logic so the report omits fields the user never saw.
function planSettingsChanges(
  original: vp.PlanSettings | undefined,
  submitted: Record<string, string | boolean> | undefined,
  currency: string,
  country: string,
  planType: 'pay as you go' | 'subscription',
  useProgressivePricing: boolean,
): Change[] {
  if (!original || !submitted) return [];
  const changes: Change[] = [];
  for (const [serviceName, section] of Object.entries(original)) {
    // Service permission toggles the form actually renders (excludes plan-model keys and
    // permissions gated out by the selected currency).
    const servicePermEntries = Object.entries(section.permissions).filter(
      ([key]) => !PLAN_MODEL_KEYS.has(key) && isPermissionAllowedForMarket(key, currency, country),
    );

    // Dependent fields are visible only when there are no service permissions, or at least one is on.
    const anyPermOn = servicePermEntries.some(([key]) => Boolean(submitted[key] ?? section.permissions[key].value));
    const fieldsVisible = servicePermEntries.length === 0 || anyPermOn;

    // Label the same way the Review/PlanSettingCard display does: a permission toggle carries no
    // backend label, so fall back to the section name (USD-prefixed for EFT rails on a USD package);
    // fees/tiers use their own backend label.
    const permissionLabel = (fee: PlanGroupFee): string => railDisplayLabel(fee.label || serviceName, currency);
    const feeLabel = (fee: PlanGroupFee): string => fee.label;

    // Permission toggles themselves are always reported (a flip is what turns the rest on/off),
    // but skip toggles the form didn't render.
    const visiblePermissions = Object.fromEntries(servicePermEntries);
    const sectionChanges: Change[] = [
      ...groupChanges(visiblePermissions, submitted, true, permissionLabel),
      ...groupChanges(section.maxAmounts, submitted, fieldsVisible, feeLabel),
      ...groupChanges(section.common, submitted, fieldsVisible, feeLabel),
      ...groupChanges(section.payAsYouGo, submitted, fieldsVisible && planType === 'pay as you go', feeLabel),
      ...groupChanges(section.subscription, submitted, fieldsVisible && planType === 'subscription', feeLabel),
      ...groupChanges(section.tiers, submitted, fieldsVisible && useProgressivePricing, feeLabel),
    ];

    // Nest every change for this section under the rail/service name (USD-prefixed for EFT on USD),
    // matching how the Review page groups rails into their own cards.
    const group = railDisplayLabel(serviceName, currency);
    for (const change of sectionChanges) change.group = group;
    changes.push(...sectionChanges);
  }
  return changes;
}

// grouped by section, as plain text to append to the business-case notes. Empty when unchanged.
export function buildBillingPackageChangeReport(billing: BillingData, country: string): string {
  const info = billing.billingPackageInfo;
  if (!info) return '';
  const pd = billing.packageDetails;

  // Plan-model gates, mirroring PackageDetails. Currency can be changed on the Agreement Details tab,
  // so prefer the submitted value (keyed by the Currency item's `settings`) over the package default.
  const planType = info.BillingSettings?.PlanType.value ?? 'subscription';
  const useProgressivePricing = info.BillingSettings?.UseProgressivePricing?.value ?? false;
  const currencySettingsKey = info.AgreementDetails?.Currency?.settings;
  const submittedCurrency = currencySettingsKey ? pd.agreementDetails?.[currencySettingsKey] : undefined;
  const currency =
    typeof submittedCurrency === 'string' ? submittedCurrency : (info.BillingSettings?.Currency.value ?? 'CAD');

  const sections = [
    { title: 'Agreement Details', changes: settingsItemChanges(info.AgreementDetails, pd.agreementDetails) },
    {
      title: 'Account Configuration',
      changes: settingsItemChanges(info.AccountConfiguration, pd.accountConfiguration, { hasControllingBoolean: true }),
    },
    {
      title: 'Account Settings',
      changes: planSettingsChanges(
        info.AccountSettings,
        pd.accountSettings,
        currency,
        country,
        planType,
        useProgressivePricing,
      ),
    },
    {
      title: 'Payment Rails',
      changes: planSettingsChanges(
        info.PaymentRails,
        pd.paymentRails,
        currency,
        country,
        planType,
        useProgressivePricing,
      ),
    },
    {
      title: 'Additional Services',
      changes: planSettingsChanges(
        info.AdditionalServices,
        pd.additionalServices,
        currency,
        country,
        planType,
        useProgressivePricing,
      ),
    },
  ].filter((s) => s.changes.length > 0);

  if (sections.length === 0) return '';

  const packageName = info.PackageSettings?.PackageName;
  const heading = packageName ? `--- Billing Package Changes (${packageName}) ---` : '--- Billing Package Changes ---';
  const lines = [heading];
  for (const section of sections) {
    lines.push(`${section.title}:`);
    let currentGroup: string | undefined;
    for (const change of section.changes) {
      if (change.group !== currentGroup) {
        currentGroup = change.group;
        if (currentGroup) lines.push(`  ${currentGroup}:`);
      }
      // Plan-setting rows nest (indent) under their rail/service heading; flat rows stay at column 0.
      const bullet = change.group ? '    - ' : '- ';
      lines.push(`${bullet}${change.label} (${change.type}): ${change.from} to ${change.to}`);
    }
  }
  return stripInvalid(lines.join('\n'));
}
